import { useEffect, useState } from 'react';
import { SLUG } from './storeConfig';
import { unitPriceInr } from './productUtils';

// The retail bag is one line per product *variant* — a shopper buying an M and an L of
// the same shirt gets two lines, unlike the corporate selection (one pick per product,
// see productUtils.readSelection). It lives in localStorage rather than sessionStorage
// so it survives closing the tab, and is dropped after a week without changes.
const KEY = `${SLUG}_bag`;
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
const CHANGE_EVENT = 'bs-bag-change';

export const lineKey = ({ productId, variantId, color, size }) =>
  [productId, variantId ?? '', color ?? '', size ?? ''].join('|');

export function readBag() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    if (!raw || !Array.isArray(raw.lines)) return [];
    if (Date.now() - (raw.updatedAt || 0) > TTL_MS) {
      localStorage.removeItem(KEY);
      return [];
    }
    return raw.lines;
  } catch {
    return [];
  }
}

function writeBag(lines) {
  try { localStorage.setItem(KEY, JSON.stringify({ lines, updatedAt: Date.now() })); } catch { /* storage blocked — bag lives for this page only */ }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Adds `line` ({ productId, variantId, color, size, sku, quantity }), merging into an
 *  existing line for the same variant. `maxQuantity` caps the merged total (stock). */
export function addToBag(line, maxQuantity = 99) {
  const lines = readBag();
  const key = lineKey(line);
  const existing = lines.find(l => l.key === key);
  if (existing) existing.quantity = Math.min(maxQuantity, existing.quantity + line.quantity);
  else lines.push({ ...line, key, quantity: Math.min(maxQuantity, line.quantity) });
  writeBag(lines);
}

export function setLineQuantity(key, quantity) {
  writeBag(readBag().map(l => (l.key === key ? { ...l, quantity: Math.max(1, quantity) } : l)));
}

export function removeLine(key) {
  writeBag(readBag().filter(l => l.key !== key));
}

export function clearBag() {
  writeBag([]);
}

/** Live bag contents — re-renders on changes from this tab and from other tabs. */
export function useBag() {
  const [lines, setLines] = useState(readBag);
  useEffect(() => {
    const sync = () => setLines(readBag());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return lines;
}

export const bagCount = (lines) => lines.reduce((n, l) => n + (l.quantity || 1), 0);

/** Joins bag lines to the current product list and flags anything that can no longer be
 *  bought as-is (product removed, variant sold out, quantity above stock), so the bag
 *  and checkout show the problem instead of the backend rejecting the whole payment. */
export function resolveLines(lines, products) {
  return lines.map(line => {
    const product = products.find(p => String(p.id) === String(line.productId));
    if (!product) return { ...line, product: null, problem: 'No longer available' };
    const variant = line.variantId != null ? product.variants?.find(v => String(v.id) === String(line.variantId)) : null;
    if (line.variantId != null && (!variant || variant.available === false)) {
      return { ...line, product, variant, problem: 'Sold out in this option' };
    }
    if (product.itemsAvailable === 0) return { ...line, product, variant, problem: 'Sold out' };
    const stock = variant?.stockQty != null ? Number(variant.stockQty) : product.itemsAvailable;
    const maxQuantity = stock != null ? stock : 99;
    const unitPrice = unitPriceInr(product, line.variantId);
    return {
      ...line, product, variant, unitPrice, maxQuantity,
      lineTotal: unitPrice * line.quantity,
      problem: line.quantity > maxQuantity ? `Only ${maxQuantity} available — reduce the quantity` : null,
    };
  });
}

/** Swaps a line to another variant (e.g. M → L) keeping its quantity, merging into an
 *  existing line for that variant if there is one. `maxQuantity` caps the result (stock). */
export function changeLineVariant(key, next, maxQuantity = 99) {
  const lines = readBag();
  const current = lines.find(l => l.key === key);
  if (!current) return;
  const nextKey = lineKey(next);
  const rest = lines.filter(l => l.key !== key);
  const existing = rest.find(l => l.key === nextKey);
  if (existing) existing.quantity = Math.min(maxQuantity, existing.quantity + current.quantity);
  else rest.splice(lines.indexOf(current), 0, { ...next, key: nextKey, quantity: Math.min(maxQuantity, current.quantity) });
  writeBag(rest);
}
